/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSession } from "../../context/AuthContext";
import { useToast } from "../providers/ToastProvider";
import Dialog from "./Dialog";
import CommentSection from "./CommentSection";
import api from "../../services/api";

interface StudentTaskViewerProps {
  taskId: string;
  title: string;
  module: string;
  description: string;
  type: "LINK" | "IMAGE" | "TEXT";
  status: string;
  requirements: string[];
  contentUrl?: string; // Deliverable Link
  evidenceUrl?: string; // Visual Proof (Screenshot)
  contentBody?: string; // Execution Summary
  feedback?: string;
  history?: Array<{
    id: string;
    status: string;
    contentUrl?: string;
    evidenceUrl?: string;
    contentBody?: string;
    feedback?: string;
    createdAt: string;
  }>;
  courseTasksCount?: number;
  completedCourseTasksCount?: number;
  expected_outcome?: string;
  student_benefits?: string[];
  coach_recommendation?: string;
  common_mistakes?: string[];
  effort_minutes?: number;
  points?: number;
  task_prompt?: { prompt: string };
}

export default function StudentTaskViewer({ taskId, title, module, description, type, status, /* requirements = [], */ contentUrl, evidenceUrl, contentBody, courseTasksCount = 0, /* completedCourseTasksCount = 0, */ points, effort_minutes , task_prompt }: StudentTaskViewerProps) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [proofUrl, setProofUrl] = useState(""); // Draft Link starts empty
  const [screenshotUrl, setScreenshotUrl] = useState(""); // Draft Image starts empty
  const [notes, setNotes] = useState(""); // Draft Notes starts empty
  const [isCopied, setIsCopied] = useState(false);
  const [showChatGPTDialog, setShowChatGPTDialog] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  // Committed States (Shown in Fulfillment Report)
  const [committedContentUrl, setCommittedContentUrl] = useState(contentUrl || "");
  const [committedEvidenceUrl, setCommittedEvidenceUrl] = useState(evidenceUrl || "");
  const [committedContentBody, setCommittedContentBody] = useState(contentBody || "");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChatInitializing, setIsChatInitializing] = useState(false);
  const [isStartingTask, setIsStartingTask] = useState(false);
  const [showMotivationModal, setShowMotivationModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [currentSubmissionId, setCurrentSubmissionId] = useState<string | null>(null);

  // Assistance & Expert States
  const [modalType, setModalType] = useState<"ASSISTANCE" | "EXPERT" | "PERSONAL_ASSISTANCE" | null>(null);
  const [showAssistanceConfirm, setShowAssistanceConfirm] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isWaitlisted, setIsWaitlisted] = useState(false);
  const [isWaitlisting, setIsWaitlisting] = useState(false);
  const { showToast } = useToast();

  const [activeRequest, setActiveRequest] = useState<any>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeJob, setActiveJob] = useState<any>(null);

  // Image Preview Modal
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl] = useState("");
  const [zoomScale, setZoomScale] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);


  const membership = (session?.user as any)?.highest_plan_name || (session?.user as any)?.membership || "FREE";
  const userId = (session?.user as any)?.id;
  const userRole = (session?.user as any)?.role;
  const isCoach = userRole === "COACH" || userRole === "ADMIN";

  // Auto-Start Task if NOT_STARTED or ASSIGNED
  useEffect(() => {
    if (currentStatus === "NOT_STARTED" || currentStatus === "ASSIGNED") {
      api.post("/submissions", {
        taskId,
        userId,
        status: "STARTED",
        contentUrl: "",
        evidenceUrl: "",
        contentBody: ""
      }).then(res => {
        if (res.data?.submission?.id) {
          setCurrentSubmissionId(res.data.submission.id);
          setCurrentStatus("STARTED");
        }
      }).catch(err => console.error("Auto-start failed", err));
    }
  }, [currentStatus, taskId, userId]);


  const [profile, setProfile] = useState<any>(null);

  // Fetch Waitlist status and active Business Profile
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      try {
        const waitlistRes = await api.get(`/vip-waitlist/status/${userId}`);
        setIsWaitlisted(waitlistRes.data.vipWaitlisted);

        const profileRes = await api.get(`/auth/status?userId=${userId}`);
        if (profileRes.data?.profile) {
          setProfile(profileRes.data.profile);
        }
      } catch (err) {
        console.error("Failed to fetch waitlist or profile status:", err);
      }
    };
    fetchData();
  }, [userId]);

  const handleJoinWaitlist = async () => {
    if (!userId) return;
    setIsWaitlisting(true);
    try {
      await api.post(`/vip-waitlist/join`, {
        userId,
        membership_plan: membership,
        task_id: taskId,
        category: profile?.category || null,
        revenue_stage: profile?.price_range || null
      });
      setIsWaitlisted(true);

      // Track vip_waitlist_joined event
      try {
        await api.post("/events/track", {
          name: "vip_waitlist_joined",
          userId,
          properties: {
            task_id: taskId,
            membership_plan: membership,
            timestamp: new Date().toISOString()
          }
        });
      } catch (err) {
        console.error("Failed to track waitlist join event:", err);
      }

      showToast("Success", "You've been added to the VIP waiting list. Our team will contact eligible members for the next VIP review session.", "success");
    } catch (err) {
      console.error(err);
      showToast("Error", "Could not join the VIP waiting list. Please try again.", "error");
    } finally {
      setIsWaitlisting(false);
    }
  };

  const handleCopyPrompt = () => {
    if (task_prompt?.prompt) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(task_prompt.prompt);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = task_prompt.prompt;
        textArea.style.position = "absolute";
        textArea.style.left = "-999999px";
        document.body.prepend(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (error) {
          console.error(error);
        } finally {
          textArea.remove();
        }
      }
      setIsCopied(true);
      setShowChatGPTDialog(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // Persistent Simulation & Live Pool Sync (Online)
  useEffect(() => {
    const syncStatus = async () => {
      if (!userId) return;
      try {
        const res = await api.get(`/service-pool?t=${Date.now()}`);
        const pool = res.data;

        const assistanceItem = pool.find((i: any) => String(i.taskId) === String(taskId) && i.type === "ASSISTANCE" && String(i.studentId) === String(userId));
        const jobItem = pool.find((i: any) => String(i.taskId) === String(taskId) && i.type === "EXPERT_JOB" && String(i.studentId) === String(userId));

        // If something was resolved/completed, trigger a full submission refresh to get the proof URLs
        const newlyResolved = (assistanceItem && assistanceItem.status === "RESOLVED") ||
          (jobItem && jobItem.status === "COMPLETED");

        if (newlyResolved && currentStatus !== "APPROVED" && currentStatus !== "COMPLETED") {
          const subRes = await api.get(`/submissions/single/${taskId}/${userId}`);
          const subData = subRes.data;
          setCommittedContentUrl(subData.contentUrl || "");
          setCommittedEvidenceUrl(subData.evidenceUrl || "");
          setCommittedContentBody(subData.contentBody || "");
          setCurrentStatus(subData.status);
          setCurrentSubmissionId(subData.id);
        }

        if (assistanceItem) setActiveRequest(assistanceItem);
        else setActiveRequest(null);

        if (jobItem) setActiveJob(jobItem);
        else setActiveJob(null);
      } catch (err) {
        console.error("Pool sync failed:", err);
      }
    };

    syncStatus();

    // Also fetch submission ID on initial load
    const fetchInitialSubmission = async () => {
      if (!userId) return;
      try {
        const res = await api.get(`/submissions/single/${taskId}/${userId}`);
        setCurrentSubmissionId(res.data.id);
      } catch (err) {
        console.warn("No initial submission found or error:", err);
      }
    };
    fetchInitialSubmission();

    const interval = setInterval(syncStatus, 5000);
    return () => clearInterval(interval);
  }, [userId, taskId, currentStatus]);


  const triggerBookAssistance = async () => {
    setShowAssistanceConfirm(false);
    setIsRequesting(true);
    try {
      const res = await api.post(`/service-pool/book`, { taskId, userId, type: "ASSISTANCE", priority: membership });
      setActiveRequest(res.data);
      showToast("Request Logged", "We have received your request. A coach will help you soon.");
    } catch (err: any) {
      showToast("Booking Blocked", "Could not request help. Please try again later.", "error");
    } finally {
      setIsRequesting(false);
    }
  };

  const handleAssignToExpert = async () => {
    if (membership !== "VIP") {
      try {
        await api.post("/events/track", {
          name: "expert_implementation_clicked",
          userId,
          properties: {
            task_id: taskId,
            membership_plan: membership,
            timestamp: new Date().toISOString()
          }
        });
      } catch (err) {
        console.error("Failed to track expert click:", err);
      }
      setModalType("EXPERT");
      return;
    }

    setIsAssigning(true);
    try {
      const res = await api.post(`/service-pool/book`, { taskId, userId, type: "EXPERT_JOB", priority: membership });
      setActiveJob(res.data);
      
      // Track VIP assignment event
      try {
        await api.post("/events/track", {
          name: "vip_member_task_assigned",
          userId,
          properties: {
            task_id: taskId,
            membership_plan: membership,
            timestamp: new Date().toISOString()
          }
        });
      } catch (err) {
        console.error("Failed to track VIP assignment event:", err);
      }

      showToast("Success", "Your implementation request has been submitted successfully.", "success");
    } catch (err: any) {
      showToast("Assignment Failed", "Could not assign an expert right now. Please try again later.", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleFinalSubmit = async () => {
    // Validation based on type
    if (type === "IMAGE" && !notes.trim()) {
      showToast("Summary Required", "Please write a quick summary of what you did to submit this task.", "error");
      return;
    }
    if (type === "LINK" && !proofUrl.trim()) {
      showToast("Link Required", "Please paste the link to your work to submit this task.", "error");
      return;
    }
    if (type === "TEXT" && !notes.trim()) {
      showToast("Summary Required", "Please write a quick summary of what you did to submit this task.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/submissions`, {
        taskId,
        userId,
        contentUrl: proofUrl, // This is now only the deliverable link
        evidenceUrl: screenshotUrl, // New field for the screenshot
        contentBody: notes,
        status: "PENDING" // Explicitly move to PENDING in DB
      });

      showToast("Submission Recorded", "Your work has been sent and is waiting for your coach to review it.", "success");
      setCommittedContentUrl(proofUrl);
      setCommittedEvidenceUrl(screenshotUrl);
      setCommittedContentBody(notes);

      // Clear Draft Form
      setProofUrl("");
      setScreenshotUrl("");
      setNotes("");

      setCurrentStatus("PENDING"); // Update UI locally
      window.dispatchEvent(new CustomEvent("taskSubmitted"));
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast("Submission Failed", err.response?.data?.error || err.message || "We could not submit your work. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartAction = async () => {
    setIsStartingTask(true);
    try {
      const res = await api.post("/submissions", {
        taskId,
        userId,
        status: "STARTED",
        contentUrl: "",
        evidenceUrl: "",
        contentBody: ""
      });
      if (res.data?.submission?.id) {
        setCurrentSubmissionId(res.data.submission.id);
        setCurrentStatus("STARTED");
        window.dispatchEvent(new CustomEvent("taskStarted"));

        // Scroll down to the submission form
        setTimeout(() => {
          const el = document.getElementById("submit-work-section");
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    } catch (err) {
      console.error("Failed to start task:", err);
      showToast("Error", "Could not start the task. Please try again.", "error");
    } finally {
      setIsStartingTask(false);
    }
  };

  const handleQuickGuidance = async () => {
    let subId = currentSubmissionId;
    if (!subId) {
      setIsChatInitializing(true);
      try {
        const res = await api.post("/submissions", {
          taskId,
          userId,
          status: currentStatus || "NOT_STARTED",
          contentUrl: "",
          evidenceUrl: "",
          contentBody: ""
        });
        if (res.data?.submission?.id) {
          subId = res.data.submission.id;
          setCurrentSubmissionId(subId);
        }
      } catch (err) {
        console.error("Failed to initialize chat submission:", err);
        showToast("Error", "Could not load guidance chat. Please try again.", "error");
        setIsChatInitializing(false);
        return;
      }
      setIsChatInitializing(false);
    }

    if (subId) {
      setIsChatOpen(true);
    }
  };

  const handleExplorePersonalAssistance = () => {
    setModalType("PERSONAL_ASSISTANCE");
  };

  const handleContinuePersonalAssistance = async () => {
    // 1. Track Click Event
    try {
      await api.post("/events/track", {
        name: "personal_assistance_clicked",
        userId,
        properties: {
          task_id: taskId,
          membership_plan: membership,
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      console.error("Failed to track personal assistance event:", err);
    }

    // 2. Open WhatsApp
    const studentName = (session?.user as any)?.name || "Member";
    const text = `Hi, I’d like to explore Personal Assistance support for my business task.

Task: ${title}
Member: ${studentName}
Membership: ${membership}`;

    const url = `https://wa.me/8075356416?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    setModalType(null);
  };

  const renderForm = () => {
    return (
      <div className="space-y-6">
        {/* Visual Proof Section Hidden */}

        {/* Deliverable Link Section */}
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
            Task Link {type === "LINK" && <span className="text-primary ml-1">(Required)</span>}
          </label>
          <div className="relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">link</span>
            <input
              type="url"
              placeholder={type === "LINK" ? "https:// (Required)" : "https:// (Optional)"}
              className="w-full pl-11 pr-5 py-4 bg-surface-container-lowest border border-outline-variant/20 rounded-[1.25rem] focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium text-on-surface placeholder:text-on-surface/40"
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
            />
          </div>
        </div>

        {/* Narrative Section */}
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
            Task Notes {type === "TEXT" && <span className="text-primary ml-1">(Required)</span>}
          </label>
          <textarea
            rows={5}
            placeholder={type === "TEXT" ? "Write what you did here..." : "Any notes about what you did... (Optional)"}
            className="w-full px-5 py-4 bg-surface-container-lowest border border-outline-variant/20 rounded-[1.25rem] focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium resize-none text-on-surface placeholder:text-on-surface/40"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    );
  };

  // Helper to parse description text, convert URLs to links, and return YouTube IDs
  const parseInstructions = (text: string, collectedYoutubeIds: string[] = []) => {
    if (!text) return { elements: null, youtubeIds: collectedYoutubeIds };

    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlRegex);

    const elements = parts.map((part, i) => {
      if (part.match(urlRegex)) {
        const href = part.startsWith('www.') ? `https://${part}` : part;
        const ytMatch = part.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([\w-]{11})/);
        if (ytMatch) {
          const videoId = ytMatch[1];
          if (!collectedYoutubeIds.includes(videoId)) {
            collectedYoutubeIds.push(videoId);
          }
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-black break-all"
            >
              {part}
            </a>
          );
        }
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-bold break-all"
          >
            {part}
          </a>
        );
      }
      return part;
    });

    return { elements, youtubeIds: collectedYoutubeIds };
  };

  const youtubeIdsList: string[] = [];
  const { elements: parsedElements } = parseInstructions(description, youtubeIdsList);

  

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <Dialog 
        isOpen={!!modalType} 
        onClose={() => setModalType(null)} 
        title={
          modalType === "EXPERT" 
            ? "⚙️ Done-For-You Setup" 
            : modalType === "PERSONAL_ASSISTANCE" 
              ? "🤝 Personal Assistance" 
              : "Personal Assistance"
        }
      >
        {modalType === "PERSONAL_ASSISTANCE" ? (
          <div className="space-y-6">
            <p className="text-slate-500 font-medium leading-relaxed text-sm">
              Get personalized guidance and implementation support for your business tasks.
            </p>
            
            <div className="space-y-4">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
                How Personal Assistance Helps
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <span className="text-sm">✅</span>
                  <p className="text-sm font-bold text-on-surface leading-tight">Personal guidance for this task</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <span className="text-sm">✅</span>
                  <p className="text-sm font-bold text-on-surface leading-tight">Step-by-step implementation support</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <span className="text-sm">✅</span>
                  <p className="text-sm font-bold text-on-surface leading-tight">1-to-1 Zoom assistance sessions</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <span className="text-sm">✅</span>
                  <p className="text-sm font-bold text-on-surface leading-tight">Faster clarity through priority support</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <span className="text-sm">✅</span>
                  <p className="text-sm font-bold text-on-surface leading-tight">Guidance focused on your current growth step</p>
                </div>
              </div>
            </div>

            <button 
              onClick={handleContinuePersonalAssistance}
              className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              Continue with Personal Assistance
            </button>
          </div>
        ) : modalType === "EXPERT" ? (
          !isWaitlisted ? (
            <div className="space-y-6 animate-in fade-in zoom-in duration-300">
              <p className="text-slate-500 font-medium leading-relaxed text-sm">
                Get advanced implementation support, systems, and expert guidance for business growth tasks that require deeper execution and automation.
              </p>

              <div className="space-y-4">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
                  How Done-For-You Setup Helps
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                    <span className="text-sm">✅</span>
                    <p className="text-sm font-bold text-on-surface leading-tight">Expert support for advanced business tasks</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                    <span className="text-sm">✅</span>
                    <p className="text-sm font-bold text-on-surface leading-tight">Help with systems and automation setup</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                    <span className="text-sm">✅</span>
                    <p className="text-sm font-bold text-on-surface leading-tight">Faster execution through implementation assistance</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                    <span className="text-sm">✅</span>
                    <p className="text-sm font-bold text-on-surface leading-tight">Priority-based expert review process</p>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                    <span className="text-sm">✅</span>
                    <p className="text-sm font-bold text-on-surface leading-tight">Guidance based on your current growth stage</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleJoinWaitlist} 
                disabled={isWaitlisting}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] hover:bg-primary/95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                {isWaitlisting ? (
                  <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">assignment_ind</span>
                    Join VIP Waiting List
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6 animate-in fade-in zoom-in duration-300">
              <div className="bg-emerald-500/10 p-8 rounded-3xl border border-emerald-500/20 text-center flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-600 animate-bounce">
                  <span className="material-symbols-outlined text-3xl font-black">verified</span>
                </div>
                <div className="space-y-2">
                  <p className="text-base font-black text-emerald-850 uppercase tracking-wider">Added to VIP Waitlist</p>
                  <p className="text-sm text-slate-600 font-semibold leading-relaxed">
                    You’ve been added to the VIP waiting list.
                    <br />
                    Our team will contact eligible members for the next VIP review session.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setModalType(null)}
                className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                Got it, thanks!
              </button>
            </div>
          )
        ) : (
          <div className="space-y-6">
            <p className="text-slate-500 font-medium leading-relaxed">
              You can ask the community for help in our official group.
            </p>
            <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10">
              <p className="text-sm font-bold text-primary mb-1">Get Support</p>
              <p className="text-xs text-slate-500 font-medium">Get 1-on-1 guidance and let our team assist you step-by-step.</p>
            </div>
            <button onClick={() => setModalType(null)} className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">Got it</button>
          </div>
        )}
      </Dialog>

      <Dialog isOpen={showAssistanceConfirm} onClose={() => setShowAssistanceConfirm(false)} title="Need a Coach's Help?">
        <div className="space-y-6">
          <p className="text-slate-500 font-medium leading-relaxed">
            Would you like a coach to help you with this task? A coach will look at your work and guide you step-by-step.
          </p>
          <div className="flex gap-4">
            <button 
              type="button"
              onClick={() => setShowAssistanceConfirm(false)} 
              className="flex-1 py-4 bg-surface-container-high text-on-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={triggerBookAssistance} 
              disabled={isRequesting}
              className="flex-1 py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRequesting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Requesting...
                </>
              ) : (
                "Yes, Ask for Help"
              )}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog isOpen={showMotivationModal} onClose={() => setShowMotivationModal(false)} title="🚀 Great Choice!">
        <div className="space-y-6 animate-in fade-in zoom-in duration-300">
          <p className="text-slate-500 font-medium leading-relaxed text-sm">
            You're about to complete one of the most important actions for growing your business.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-surface-container-high border border-outline-variant/10 rounded-2xl shadow-sm">
              <span className="material-symbols-outlined text-amber-500">schedule</span>
              <p className="text-sm font-bold text-on-surface">⏱ Estimated Time: {effort_minutes || 15} Minutes</p>
            </div>

            <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl">
              <span className="text-lg">🎯</span>
              <p className="text-sm font-bold text-on-surface leading-relaxed">
                After completing this action, submit your proof to your coach for review.
                <br /><br />
                Your coach will verify your work and help you if anything needs improvement.
              </p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl relative overflow-hidden">
              <div className="absolute -right-4 -top-4 opacity-10 text-6xl">💬</div>
              <p className="text-sm font-bold text-blue-900 dark:text-blue-200 italic leading-relaxed z-10 relative">
                "Done is better than perfect. Take action today."
                <br />
                <span className="text-xs font-black not-italic tracking-widest uppercase mt-2 block">— Joseph Mary John</span>
              </p>
            </div>

            {task_prompt?.prompt && (
              <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-2xl shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-sm">smart_toy</span>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-bold text-on-surface">AI Prompt Ready</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Copy it before starting</p>
                  </div>
                </div>
                <button 
                  onClick={handleCopyPrompt}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-bold shadow-md shadow-primary/20 hover:scale-105 hover:bg-primary/90 transition-all flex items-center gap-2 cursor-pointer"
                >
                  {isCopied ? (
                    <>
                      <span className="material-symbols-outlined text-[14px]">check_circle</span>
                      Copied!
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[14px]">content_copy</span>
                      Copy Prompt
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              setShowMotivationModal(false);
              handleStartAction();
            }}
            disabled={isStartingTask}
            className="w-full py-5 bg-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] hover:bg-primary/95 transition-all flex items-center justify-center gap-2 cursor-pointer mt-4"
          >
            <span className="text-lg">🚀</span>
            Let's Start
          </button>
        </div>
      </Dialog>

      <Dialog isOpen={showChatGPTDialog} onClose={() => setShowChatGPTDialog(false)} title="Prompt Copied!">
        <div className="space-y-6 animate-in fade-in zoom-in duration-300">
          <p className="text-slate-500 font-medium leading-relaxed text-center text-sm">
            The AI prompt has been copied to your clipboard. Click below to open ChatGPT and paste it in.
          </p>
          <div className="flex gap-4">
            <button 
              type="button"
              onClick={() => setShowChatGPTDialog(false)} 
              className="flex-1 py-4 bg-surface-container-high text-on-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all cursor-pointer"
            >
              Close
            </button>
            <a 
              href="https://chatgpt.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowChatGPTDialog(false)}
              className="flex-1 py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span className="material-symbols-outlined text-[16px]">smart_toy</span>
              Open ChatGPT
            </a>
          </div>
        </div>
      </Dialog>

      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <nav className="flex flex-wrap items-center gap-2 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors group"
            >
              <span className="material-symbols-outlined text-[14px] transition-transform group-hover:-translate-x-1">arrow_back</span>
              Back
            </button>
            <span className="text-slate-300 text-[10px]">/</span>
            <Link to="/dashboard" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Academy</Link>
            <span className="text-slate-300 text-[10px]">/</span>
            <Link to="/tasks" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Tasks</Link>
            <span className="text-slate-300 text-[10px]">/</span>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Task View</span>
          </nav>
          <h2 className="text-3xl font-black text-on-surface tracking-tighter">{title}</h2>
          <p className="text-xs text-slate-400 font-semibold mt-1 italic tracking-tight">✨ Progress matters more than perfection.</p>
        </div>
                <div className="sm:ml-auto flex flex-col items-end gap-3">
          
          <div className={`w-fit px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shrink-0 ${currentStatus === "REJECTED" ? "bg-red-500/10 text-red-500" :
            (activeJob?.status || activeRequest?.status || currentStatus) === "APPROVED" ? "bg-emerald-500/10 text-emerald-500" :
              (committedContentUrl || committedEvidenceUrl || committedContentBody) ? "bg-violet-600/10 text-violet-600 border border-violet-600/20" :
                (activeJob?.status || activeRequest?.status || currentStatus) === "ASSIGNED" ? "bg-blue-500/10 text-blue-500" :
                  (activeJob?.status || activeRequest?.status || currentStatus) === "OPEN" || (activeJob?.status || activeRequest?.status || currentStatus) === "PENDING" ? "bg-amber-500/10 text-amber-500" :
                    "bg-primary/10 text-primary"
          }`}>
          <div className={`w-2 h-2 rounded-full ${currentStatus === "REJECTED" ? "bg-red-500" :
              (activeJob?.status || activeRequest?.status || currentStatus) === "APPROVED" ? "bg-emerald-500" :
                (committedContentUrl || committedEvidenceUrl || committedContentBody) ? "bg-violet-600 shadow-[0_0_8px_rgba(139,92,246,0.5)]" :
                  (activeJob?.status || activeRequest?.status || currentStatus) === "ASSIGNED" ? "bg-blue-500" :
                    (activeJob?.status || activeRequest?.status || currentStatus) === "OPEN" || (activeJob?.status || activeRequest?.status || currentStatus) === "PENDING" ? "bg-amber-500" : "bg-primary"
            }`} />
          {currentStatus === "REJECTED" ? "CHANGES NEEDED" : (
            (committedContentUrl || committedEvidenceUrl || committedContentBody) ? "SUBMITTED" : (
              activeJob?.status || activeRequest?.status || currentStatus
            )
          )}
        </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 space-y-6">
          {/* New Flow */}
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8">
            {/* 1. Hero Section Removed */}
            
            {/* 2. Joseph Speaks Removed */}

            {/* 3. Big CTA Removed */}

            {/* 4. Expected Outcome Removed */}

            {/* 5. How this helps Removed */}

            {/* 6. Today's Mission */}
            <div className="space-y-4 pt-4">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">📋 Today's Mission</h3>
              <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-3xl shadow-sm space-y-8">
                <div className="text-base text-slate-500 leading-relaxed font-medium whitespace-pre-wrap">{parsedElements}</div>
                
                {/* AI Prompt */}
                {task_prompt?.prompt && (
                  <div className="space-y-4 pt-6 border-t border-outline-variant/10">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">smart_toy</span> AI Prompt Template
                      </p>
                      <button 
                        onClick={handleCopyPrompt}
                        className="px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary hover:text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <span className="material-symbols-outlined text-[14px]">
                          {isCopied ? "check" : "content_copy"}
                        </span> 
                        {isCopied ? "Copied!" : "Copy Prompt"}
                      </button>
                    </div>
                    <div className="bg-slate-900 text-slate-300 p-6 rounded-2xl font-mono text-sm shadow-inner border border-slate-800 relative">
                      <div className={`whitespace-pre-wrap ${isPromptExpanded ? "" : "line-clamp-3"}`}>
                        {task_prompt.prompt}
                      </div>
                      {!isPromptExpanded && (
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-900 to-transparent rounded-b-2xl pointer-events-none" />
                      )}
                    </div>
                    <button
                      onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                      className="text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-primary transition-colors flex items-center gap-1 w-full justify-center pt-2"
                    >
                      {isPromptExpanded ? "Show Less" : "Show More"}
                      <span className="material-symbols-outlined text-[16px]">
                        {isPromptExpanded ? "expand_less" : "expand_more"}
                      </span>
                    </button>
                  </div>
                )}
                
                {/* Attached Videos */}
                {youtubeIdsList.length > 0 && (
                  <div className="space-y-4 pt-6 border-t border-outline-variant/10">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Attached Videos</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {youtubeIdsList.map((vid) => (
                        <div 
                          key={vid}
                          onClick={() => setSelectedVideoId(vid)}
                          className="group relative cursor-pointer overflow-hidden rounded-2xl border border-outline-variant/10 aspect-video shadow-md bg-black"
                        >
                          <img 
                            src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`} 
                            alt="Video Tutorial" 
                            className="w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-300"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-white/95 text-black flex items-center justify-center shadow-xl group-hover:bg-primary group-hover:text-white transition-all transform group-hover:scale-110">
                              <span className="material-symbols-outlined text-3xl font-black ml-1">play_arrow</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                
              </div>
            </div>

            {/* 7. Mistakes Removed */}

            {/* 8. Submit Your Work */}
            {currentStatus !== "APPROVED" && currentStatus !== "COMPLETED" && (
              <div id="submit-work-section" className="space-y-6 pt-10 border-t border-outline-variant/10">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">📸 Submit Your Work</h3>
                
                  <div id="tour-final-submission" className="bg-surface-container-low p-8 rounded-3xl shadow-sm space-y-8 border border-outline-variant/10">
                    <div>
                      <h3 className="text-lg font-black text-slate-950 tracking-tight">Share Your Progress</h3>
                      <p className="text-[11px] text-slate-500 font-bold tracking-tight mt-1">Don't worry about perfection. Submit your best version.</p>
                    </div>
                    {renderForm()}
                    <button
                      onClick={handleFinalSubmit}
                      disabled={isSubmitting}
                      className={`w-full py-6 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 ${isSubmitting ? "bg-slate-400 cursor-not-allowed" : "bg-primary shadow-primary/20 hover:scale-[1.02] active:scale-[0.98]"}`}
                    >
                      {isSubmitting ? (
                        <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-lg">rocket_launch</span>
                          Submit for Review
                        </>
                      )}
                    </button>
                  </div>
              </div>
            )}

            {/* 9. Celebration Preview */}
            {(currentStatus !== "APPROVED" && currentStatus !== "COMPLETED") && (
              <div className="space-y-4 pt-6">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">🎉 Your Reward</h3>
                <div className="p-8 bg-surface-container-highest/20 border border-outline-variant/10 rounded-3xl flex flex-wrap items-center gap-8 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 shrink-0">
                      <span className="material-symbols-outlined text-xl">star</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Experience</p>
                      <p className="text-xl font-black text-on-surface">+{points || 100} XP</p>
                    </div>
                  </div>
                  <div className="w-px h-12 bg-outline-variant/20 hidden sm:block" />
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                      <span className="material-symbols-outlined text-xl">trending_up</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate max-w-[100px]">{module}</p>
                      <p className="text-xl font-black text-on-surface">+{courseTasksCount > 0 ? Math.round(100 / courseTasksCount) : 0}%</p>
                    </div>
                  </div>
                  <div className="w-px h-12 bg-outline-variant/20 hidden lg:block" />
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-violet-500/10 flex items-center justify-center text-violet-500 shrink-0">
                      <span className="material-symbols-outlined text-xl">lock_open</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unlocks</p>
                      <p className="text-lg font-black text-on-surface">Next Best Action</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
<div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
          <div className="space-y-4">
            {/* Need Help Section */}
            {(status !== "APPROVED" && status !== "COMPLETED" && !isCoach) && (
              <div className="bg-surface-container-low p-6 rounded-3xl border border-outline-variant/10 shadow-sm space-y-4">
                <div className="text-left px-2">
                  <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest">Need Help With This Task?</h4>
                  <p className="text-[10px] text-slate-500 font-bold tracking-tight mt-0.5">Choose the support level that matches your current need.</p>
                </div>

                <div className="space-y-3">
                  {/* Button 1: Quick Guidance */}
                  <button
                    onClick={handleQuickGuidance}
                    disabled={isChatInitializing}
                    className="w-full py-4 bg-sky-600 hover:bg-sky-700 text-white rounded-[2rem] font-black transition-all shadow-xl shadow-sky-600/10 flex flex-col items-center justify-center gap-0.5 group active:scale-98 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChatInitializing ? (
                      <div className="flex items-center gap-2 animate-pulse">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs uppercase tracking-widest animate-pulse">Opening Guidance...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm font-black group-hover:scale-110 transition-transform">forum</span>
                          <span className="text-xs uppercase tracking-widest">💬 Quick Guidance</span>
                        </div>
                        <span className="text-[9px] font-medium text-white/85 lowercase tracking-tight">Small doubts & quick help</span>
                      </>
                    )}
                  </button>

                  {/* Button 2: Personal Assistance */}
                  {!activeRequest && (
                    <button 
                      id="tour-book-assistance" 
                      onClick={handleExplorePersonalAssistance} 
                      className="w-full py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-[2rem] font-black transition-all shadow-xl shadow-amber-600/10 flex flex-col items-center justify-center gap-0.5 group active:scale-98 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-widest">🤝 Personal Assistance</span>
                      </div>
                      <span className="text-[9px] font-medium text-white/80 lowercase tracking-tight">Personal support for implementation</span>
                    </button>
                  )}

                  {/* Button 3: Done-For-You Setup */}
                  {!activeJob && (
                    <button 
                      id="tour-assign-expert" 
                      onClick={handleAssignToExpert} 
                      disabled={isAssigning} 
                      className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-[2rem] font-black transition-all shadow-xl shadow-slate-900/10 flex flex-col items-center justify-center gap-0.5 group active:scale-98 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAssigning ? (
                        <div className="flex items-center gap-2 animate-pulse">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs uppercase tracking-widest animate-pulse">Assigning...</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-xs uppercase tracking-widest">⚙️ Done-For-You Setup</span>
                          </div>
                          <span className="text-[9px] font-medium text-white/80 lowercase tracking-tight">Advanced systems & execution support</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeJob && (
              <div className={`rounded-3xl p-8 shadow-sm transition-all flex flex-col justify-between gap-6 ${currentStatus === "REJECTED" ? "bg-red-500" :
                  (committedContentUrl || committedEvidenceUrl || committedContentBody) ? "bg-violet-600 shadow-xl shadow-violet-500/20" :
                    activeJob.status === "ASSIGNED" ? "bg-blue-500" :
                      (activeJob.status === "COMPLETED" || activeJob.status === "RESOLVED") ? "bg-emerald-500" :
                        "bg-amber-500"
                }`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-white/60 uppercase tracking-[0.2em]">Expert Request</span>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-xs text-white/60">schedule</span>
                    <span className="text-[10px] font-bold text-white/60">
                      {activeJob.createdAt ? new Date(activeJob.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Recently"}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-white/70 uppercase tracking-[0.15em] opacity-80">Request ID</p>
                    <h4 className="text-4xl font-extrabold text-white tracking-tighter leading-tight">#{activeJob.id?.slice(-6)?.toUpperCase() || "PENDING"}</h4>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/20 text-white border border-white/10 shadow-sm">
                      {activeJob.priority || membership}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">{activeJob.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeRequest && (
              <div className={`rounded-3xl p-8 shadow-sm transition-all flex flex-col justify-between gap-6 ${currentStatus === "REJECTED" ? "bg-red-500" :
                  (committedContentUrl || committedEvidenceUrl || committedContentBody) ? "bg-violet-600 shadow-xl shadow-violet-500/20" :
                    activeRequest.status === "ASSIGNED" ? "bg-blue-500" :
                      (activeRequest.status === "COMPLETED" || activeRequest.status === "RESOLVED") ? "bg-emerald-500" :
                        "bg-amber-500"
                }`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-white/60 uppercase tracking-[0.2em]">Help Request</span>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-xs text-white/60">schedule</span>
                    <span className="text-[10px] font-bold text-white/60">
                      {activeRequest.createdAt ? new Date(activeRequest.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Recently"}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-white/70 uppercase tracking-[0.15em] opacity-80">Request ID</p>
                    <h4 className="text-4xl font-extrabold text-white tracking-tighter leading-tight">#{activeRequest.id?.slice(-6)?.toUpperCase() || activeRequest.token?.toUpperCase() || "PENDING"}</h4>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/20 text-white border border-white/10 shadow-sm">
                      {activeRequest.priority || membership}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full bg-white animate-pulse ${(committedContentUrl || committedEvidenceUrl || committedContentBody) ? "opacity-100" : "opacity-40"
                        }`} />
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">
                        {(committedContentUrl || committedEvidenceUrl || committedContentBody) ? "SUBMITTED" : (activeRequest.status || "OPEN")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          

        </div>
      </div>

      {/* Floating Protocol Discussion Bubble */}
      {currentSubmissionId && mounted && (
        <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end gap-4">
          {/* Chat Popover */}
          {isChatOpen && (
            <div className="w-[min(400px,90vw)] h-[min(600px,calc(100vh-160px))] bg-surface-container-lowest border border-outline-variant/20 rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-500 mb-2">
              <div className="bg-on-surface p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-orange-500">forum</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-surface tracking-tight">Get Guidance</h4>
                    <p className="text-[9px] font-black uppercase tracking-widest text-surface/40">Chat with Support Team</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsChatOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all flex items-center justify-center"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-6 bg-surface-container-low">
                <CommentSection 
                  submissionId={currentSubmissionId} 
                  hideHeader={true} 
                  welcomeMessages={[
                    { role: 'coach', text: `👋 Hi ${((session?.user as any)?.name || "Support").split(" ")[0]}, need help with this task? 😊` },
                    { role: 'coach', text: `Ask your doubt here.\nOur team will message you back when available.` }
                  ]}
                />
              </div>
            </div>
          )}
 
          {/* Floating Action Button */}
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className={`w-16 h-16 rounded-[1.75rem] shadow-2xl flex items-center justify-center transition-all duration-500 hover:scale-110 active:scale-95 group relative ${
              isChatOpen ? "bg-on-surface text-surface rotate-90" : "bg-orange-500 text-white"
            }`}
          >
            <span className="material-symbols-outlined text-2xl">
              {isChatOpen ? "close" : "forum"}
            </span>
            
            {/* Tooltip */}
            {!isChatOpen && (
              <div className="absolute right-20 bg-on-surface text-surface px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all pointer-events-none whitespace-nowrap shadow-xl">
                Open Chat
              </div>
            )}
          </button>
        </div>
      )}

      {/* Cinema Mode Evidence Viewer */}
      {mounted && isPreviewOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center animate-in fade-in duration-300 overflow-hidden cursor-zoom-out backdrop-blur-xl"
          onClick={() => {
            setIsPreviewOpen(false);
            setZoomScale(1);
          }}
        >
          {/* Close Button Top Right */}
          <button
            className="absolute top-8 right-8 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur-xl border border-white/10 hover:bg-white hover:text-black transition-all z-50"
            onClick={(e) => {
              e.stopPropagation();
              setIsPreviewOpen(false);
              setZoomScale(1);
            }}
          >
            <span className="material-symbols-outlined">close</span>
          </button>

          <img
            src={previewUrl}
            alt="Evidence Full View"
            style={{ transform: `scale(${zoomScale})` }}
            className="max-w-full max-h-full object-contain shadow-2xl animate-in zoom-in-95 duration-500 transition-transform cursor-default"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Controls Bottom Right */}
          <div
            className="absolute bottom-10 right-10 flex items-center gap-2 p-2 bg-white/10 backdrop-blur-2xl border border-white/10 rounded-2xl z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomScale(prev => Math.max(0.2, prev - 0.2))}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Zoom Out"
            >
              <span className="material-symbols-outlined">zoom_out</span>
            </button>
            <div className="w-12 text-center text-[10px] font-black text-white uppercase tracking-tighter">
              {Math.round(zoomScale * 100)}%
            </div>
            <button
              onClick={() => setZoomScale(prev => Math.min(5, prev + 0.2))}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Zoom In"
            >
              <span className="material-symbols-outlined">zoom_in</span>
            </button>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <button
              onClick={() => setZoomScale(1)}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Reset Zoom"
            >
              <span className="material-symbols-outlined text-sm">restart_alt</span>
            </button>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <a
              href={previewUrl}
              download
              className="w-10 h-10 rounded-xl bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-lg"
              title="Download Evidence"
            >
              <span className="material-symbols-outlined text-sm">download</span>
            </a>
          </div>
        </div>
      )}

      {/* Video Overlay Modal */}
      {mounted && selectedVideoId && (
        <div 
          className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4 backdrop-blur-xl animate-in fade-in duration-300"
          onClick={() => setSelectedVideoId(null)}
        >
          <div 
            className="relative w-full max-w-4xl aspect-video bg-black rounded-3xl overflow-hidden border border-white/10 shadow-2xl animate-in zoom-in-95 duration-500"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setSelectedVideoId(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-xl border border-white/10 hover:bg-white hover:text-black transition-all"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
            <iframe 
              width="100%" 
              height="100%" 
              src={`https://www.youtube.com/embed/${selectedVideoId}?autoplay=1`}
              title="YouTube video player" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
